import AddRounded from '@mui/icons-material/AddRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { VariantPicker, type Variant as Pickable } from '../../components/pickers';
import { money } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { catalogKeys, type Product } from './model';
import { SectionTitle } from './ui';

interface Item {
  key: string;
  variant: { id: string; name: string } | null;
  extraPrice: string;
}
interface Group {
  key: string;
  name: string;
  items: Item[];
}

let seq = 0;
const k = () => `c${String(++seq)}`;
const blankItem = (): Item => ({ key: k(), variant: null, extraPrice: '' });
const blankGroup = (): Group => ({ key: k(), name: '', items: [blankItem()] });

/**
 * A combo's choice groups (Odoo-style): e.g. "Main", "Drink", "Side", each listing the items the
 * cashier can pick one of, with what each adds to the combo price.
 */
export function ComboPanel({ product: p }: { product: Product }) {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const qc = useQueryClient();
  const canUpdate = can('products.update');
  const [groups, setGroups] = useState<Group[]>(() =>
    p.combo?.groups.length
      ? p.combo.groups.map((g) => ({
          key: k(),
          name: g.name,
          items: g.items.map((i) => ({
            key: k(),
            variant: { id: i.variantId, name: i.name },
            extraPrice: Number(i.extraPrice) > 0 ? String(Number(i.extraPrice)) : '',
          })),
        }))
      : [blankGroup()],
  );
  const [error, setError] = useState<string | null>(null);
  const price = p.variants[0]?.salePrice ?? '0';

  const setGroup = (key: string, change: Partial<Group>) =>
    setGroups((gs) => gs.map((g) => (g.key === key ? { ...g, ...change } : g)));
  const setItem = (gKey: string, iKey: string, change: Partial<Item>) =>
    setGroups((gs) =>
      gs.map((g) =>
        g.key === gKey
          ? { ...g, items: g.items.map((i) => (i.key === iKey ? { ...i, ...change } : i)) }
          : g,
      ),
    );

  const save = useMutation({
    mutationFn: (body: { name: string; items: { variantId: string; extraPrice: string }[] }[]) =>
      unwrap(
        api.PUT('/products/{id}/combo', {
          params: { path: { id: p.id } },
          body: { groups: body, version: p.version },
        }),
      ),
    onSuccess: async () => {
      notify.success(`Choices of ${p.name} saved`);
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const submit = () => {
    const filled = groups
      .map((g) => ({ ...g, items: g.items.filter((i) => i.variant || i.extraPrice.trim()) }))
      .filter((g) => g.name.trim() || g.items.length);
    const problem = filled.some((g) => !g.name.trim())
      ? 'Name every group (e.g. Drink).'
      : filled.some((g) => g.items.length === 0)
        ? 'Each group needs at least one item.'
        : filled.some((g) => g.items.some((i) => !i.variant))
          ? 'Pick a product on every item line.'
          : filled.some((g) =>
                g.items.some((i) => i.extraPrice.trim() && !(Number(i.extraPrice) >= 0)),
              )
            ? 'Extra prices are amounts of 0 or more.'
            : filled.some((g) => new Set(g.items.map((i) => i.variant?.id)).size !== g.items.length)
              ? 'An item can be listed once per group.'
              : null;
    setError(problem);
    if (!problem)
      save.mutate(
        filled.map((g) => ({
          name: g.name.trim(),
          items: g.items.map((i) => ({
            variantId: i.variant?.id ?? '',
            extraPrice: i.extraPrice.trim() || '0',
          })),
        })),
      );
  };

  return (
    <Stack spacing={2.5}>
      <Alert severity="info" variant="outlined">
        Selling {p.name} ({money(price, format)}) asks the cashier to pick one item from each group.
        Stock comes off the picked items, and the price is shared between them by their own prices;
        an extra price is added on top.
      </Alert>
      {error && <Alert severity="error">{error}</Alert>}
      {groups.map((g, gi) => (
        <Card key={g.key}>
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', mb: 2 }}>
              <TextField
                label={`Group ${String(gi + 1)} name`}
                placeholder="e.g. Drink"
                value={g.name}
                onChange={(e) => setGroup(g.key, { name: e.target.value })}
                sx={{ flex: 1, maxWidth: 360 }}
                disabled={!canUpdate}
              />
              {canUpdate && (
                <Tooltip title="Remove group">
                  <IconButton
                    aria-label={`Remove group ${String(gi + 1)}`}
                    onClick={() => setGroups((gs) => gs.filter((x) => x.key !== g.key))}
                    sx={{ mt: 1 }}
                  >
                    <DeleteOutlineRounded />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <SectionTitle title="Choices" hint="The cashier picks one of these." />
            <Stack spacing={1.5}>
              {g.items.map((i, ii) => (
                <Stack
                  key={i.key}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  sx={{ alignItems: { sm: 'flex-start' } }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <VariantPicker
                      label={`Group ${String(gi + 1)} item ${String(ii + 1)}`}
                      value={
                        i.variant
                          ? ({
                              id: i.variant.id,
                              displayName: i.variant.name,
                              sku: '',
                            } as unknown as Pickable)
                          : null
                      }
                      onChange={(v) => {
                        const s = v as unknown as { id: string; displayName: string } | null;
                        setItem(g.key, i.key, {
                          variant: s ? { id: s.id, name: s.displayName } : null,
                        });
                      }}
                    />
                  </Box>
                  <TextField
                    label="Extra price"
                    value={i.extraPrice}
                    onChange={(e) => setItem(g.key, i.key, { extraPrice: e.target.value })}
                    placeholder="0"
                    sx={{ width: { sm: 170 } }}
                    disabled={!canUpdate}
                    slotProps={{
                      htmlInput: {
                        inputMode: 'decimal',
                        'aria-label': `Extra price, group ${String(gi + 1)} item ${String(ii + 1)}`,
                      },
                      input: {
                        startAdornment: <InputAdornment position="start">+</InputAdornment>,
                      },
                    }}
                  />
                  {canUpdate && (
                    <Tooltip title="Remove item">
                      <IconButton
                        aria-label={`Remove item ${String(ii + 1)} of group ${String(gi + 1)}`}
                        onClick={() =>
                          setGroup(g.key, { items: g.items.filter((x) => x.key !== i.key) })
                        }
                        sx={{ mt: 1 }}
                      >
                        <DeleteOutlineRounded />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              ))}
            </Stack>
            {canUpdate && (
              <Button
                startIcon={<AddRounded />}
                sx={{ mt: 1.5 }}
                disabled={g.items.length >= 30}
                onClick={() => setGroup(g.key, { items: [...g.items, blankItem()] })}
              >
                Add a choice
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
      {canUpdate && (
        <Stack direction="row" spacing={1.5} sx={{ justifyContent: 'space-between' }}>
          <Button
            startIcon={<AddRounded />}
            disabled={groups.length >= 10}
            onClick={() => setGroups((gs) => [...gs, blankGroup()])}
          >
            Add a group
          </Button>
          <Button variant="contained" disabled={save.isPending} onClick={submit}>
            Save choices
          </Button>
        </Stack>
      )}
      {groups.length === 0 && (
        <Typography color="text.secondary">
          No groups: the combo can’t be sold until it has at least one.
        </Typography>
      )}
    </Stack>
  );
}
